import {CONSENT_CHANGED_EVENT, getConsent, subscribe} from '../cookieConsent/store'

// Google Analytics 4 — non-essential tracking, must NOT load until the user has explicitly
// consented to analytics. Mirrors fe/common/errors/sentry.ts: idempotent init on consent grant,
// runtime `enabled` flag every trackEvent callsite checks, lazy script injection.
//
// Consent Mode v2 defaults (analytics_storage: denied, ad_storage: denied, etc.) are set in
// index.html BEFORE this module runs, so even a Mark1 GA pageview after script load respects
// the user's choice. Here we flip analytics_storage to granted/denied via gtag('consent','update').

type GtagFn = (...args: unknown[]) => void

declare global {
  interface Window {
    dataLayer?: unknown[]
    gtag?: GtagFn
  }
}

let initialized = false
let enabled = false
let activeAppName: 'gui' | 'render' | null = null

const getMeasurementId = (): string | undefined => {
  const id = import.meta.env.VITE_GA_MEASUREMENT_ID
  return typeof id === 'string' && id.length > 0 ? id : undefined
}

const isDebugMode = (): boolean => import.meta.env.VITE_GA_DEBUG_MODE === 'true'

const ensureGtagStub = (): void => {
  if (typeof window === 'undefined') return
  window.dataLayer = window.dataLayer || []
  if (!window.gtag) {
    window.gtag = function gtag(...args) {
      window.dataLayer!.push(args)
    }
  }
}

const injectScript = (measurementId: string): void => {
  if (typeof document === 'undefined') return
  const id = 'ga4-loader'
  if (document.getElementById(id)) return
  const script = document.createElement('script')
  script.id = id
  script.async = true
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`
  document.head.appendChild(script)
}

const clearGaCookies = (): void => {
  if (typeof document === 'undefined') return
  const expires = 'expires=Thu, 01 Jan 1970 00:00:00 GMT'
  const path = 'path=/'
  const host = window.location.hostname
  // Clear on bare host and on a leading-dot variant (GA writes with leading dot on registrable domains).
  const domains = [host, `.${host}`]
  // _ga and per-property _ga_<MID> cookies — wildcard match by iterating document.cookie.
  const names = document.cookie
    .split(';')
    .map((c) => c.trim().split('=')[0])
    .filter((n) => n === '_ga' || n.startsWith('_ga_') || n === '_gid' || n === '_gat')
  for (const name of names) {
    for (const domain of domains) {
      document.cookie = `${name}=; ${expires}; ${path}; domain=${domain}`
    }
    document.cookie = `${name}=; ${expires}; ${path}`
  }
}

const initGa = (appName: 'gui' | 'render'): void => {
  if (initialized) {
    enabled = true
    return
  }
  const measurementId = getMeasurementId()
  if (!measurementId) return
  initialized = true
  enabled = true
  activeAppName = appName

  ensureGtagStub()
  injectScript(measurementId)

  window.gtag!('js', new Date())
  window.gtag!('consent', 'update', {analytics_storage: 'granted'})
  window.gtag!('config', measurementId, {
    app_name: appName,
    anonymize_ip: true,
    send_page_view: true,
    debug_mode: isDebugMode() || undefined,
  })
}

const disableGa = (): void => {
  if (!enabled) return
  enabled = false
  if (window.gtag) {
    window.gtag('consent', 'update', {analytics_storage: 'denied'})
  }
  clearGaCookies()
}

const applyConsent = (consented: boolean): void => {
  if (consented) {
    if (!activeAppName) return
    initGa(activeAppName)
  } else {
    disableGa()
  }
}

/**
 * Gate GA behind the user's analytics consent. Reads the synchronous LS-backed consent store
 * on call; subscribes to subsequent consent changes so toggling on /cookies takes effect
 * without a reload. Safe to call from main.tsx during module init.
 */
export const initGaIfConsented = (appName: 'gui' | 'render'): void => {
  if (typeof window === 'undefined') return
  activeAppName = appName
  ensureGtagStub()
  const consent = getConsent()
  if (consent?.analytics) initGa(appName)
  subscribe(() => {
    const next = getConsent()
    applyConsent(next?.analytics === true)
  })
  window.addEventListener(CONSENT_CHANGED_EVENT, () => {
    const next = getConsent()
    applyConsent(next?.analytics === true)
  })
}

export const isGaEnabled = (): boolean => enabled

// GA4's automatic `send_page_view` only fires once, for the URL present at init — it does not
// know about client-side route changes. Call this from a router-level location listener so
// SPA navigation between pages (e.g. clicking a footer/related-guide link) is still counted,
// not just full page loads / direct landings.
export const trackPageView = (path: string): void => {
  if (!enabled || !window.gtag) return
  window.gtag('event', 'page_view', {
    page_location: `${window.location.origin}${path}`,
    page_path: path,
    page_title: document.title,
  })
}

export const setGaUserId = (uid: string | null): void => {
  if (!enabled || !window.gtag) return
  const measurementId = getMeasurementId()
  if (!measurementId) return
  window.gtag('config', measurementId, {user_id: uid ?? undefined})
}

export const setGaUserProperties = (props: Record<string, unknown>): void => {
  if (!enabled || !window.gtag) return
  window.gtag('set', 'user_properties', props)
}
